import type {
  GroupMemberPoints,
  ItemCard,
  ItemCardSet,
  MemberCoinRecord,
  MemberItemInstance,
  PointLevelSettings,
} from '../types/pointItemNote';
import { remainingUseCountForNewItem } from './memberItemStats';

function levelFromTotal(total: number, unit: number): number {
  const u = Math.max(1, unit || 50);
  return Math.floor(Math.max(0, total) / u);
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function sameStringArray(a: string[] | undefined, b: string[] | undefined): boolean {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i += 1) {
    if (aa[i] !== bb[i]) return false;
  }
  return true;
}

function changedRewardLevels(
  prevSettings: PointLevelSettings,
  newSettings: PointLevelSettings
): number[] | null {
  if ((prevSettings.levelUpPoints || 50) !== (newSettings.levelUpPoints || 50)) {
    return null;
  }

  const levels = new Set<number>();
  const prevCoinLevels = Object.keys(prevSettings.levelCoins ?? {}).map(Number);
  const nextCoinLevels = Object.keys(newSettings.levelCoins ?? {}).map(Number);
  const prevItemLevels = Object.keys(prevSettings.levelItemCards ?? {}).map(Number);
  const nextItemLevels = Object.keys(newSettings.levelItemCards ?? {}).map(Number);

  [...prevCoinLevels, ...nextCoinLevels, ...prevItemLevels, ...nextItemLevels].forEach((level) => {
    if (!Number.isFinite(level) || level < 1) return;
    const prevCoin = Number(prevSettings.levelCoins?.[level]) || 0;
    const nextCoin = Number(newSettings.levelCoins?.[level]) || 0;
    const prevItems = prevSettings.levelItemCards?.[level] ?? [];
    const nextItems = newSettings.levelItemCards?.[level] ?? [];
    if (prevCoin !== nextCoin || !sameStringArray(prevItems, nextItems)) {
      levels.add(level);
    }
  });

  return Array.from(levels).sort((a, b) => a - b);
}

function catalogById(itemCards: ItemCardSet): Map<string, ItemCard> {
  const map = new Map<string, ItemCard>();
  [...itemCards.levelupItem, ...itemCards.assignmentItem, ...itemCards.storeItem].forEach((c) => {
    map.set(c.itemCardId, c);
  });
  return map;
}

function grantLevelItemInstance(
  memberId: string,
  itemCardId: string,
  level: number,
  now: string,
  catalog: Map<string, ItemCard>
): MemberItemInstance {
  const card = catalog.get(itemCardId);
  return {
    id: newId('item'),
    memberId,
    itemCardId,
    category: 'levelup',
    acquiredLevel: level,
    remainingUseCount: remainingUseCountForNewItem(card),
    isLocked: false,
    acquiredAt: now,
  };
}

/**
 * 레벨업/다운에 따른 Levelup 코인·아이템 동기화.
 * - 레벨업: 코인 지급 + 아이템 지급/잠금 해제
 * - 레벨다운: 코인 회수 + 아이템 잠금, rewardedLevels에서 제거 → 재레벨업 시 재지급
 */
export function syncLevelUpRewardsForMember(
  group: GroupMemberPoints,
  memberId: string,
  opts: {
    oldTotal: number;
    newTotal: number;
    levelSettings: PointLevelSettings;
    itemCards: ItemCardSet;
    recordedDate?: string;
  }
): GroupMemberPoints {
  const unit = opts.levelSettings.levelUpPoints || 50;
  const oldLevel = levelFromTotal(opts.oldTotal, unit);
  const newLevel = levelFromTotal(opts.newTotal, unit);
  if (oldLevel === newLevel) return group;

  const date = opts.recordedDate || todayIso();
  const now = new Date().toISOString();
  const levelCoins = opts.levelSettings.levelCoins ?? {};
  const levelItemCards = opts.levelSettings.levelItemCards ?? {};
  const catalog = catalogById(opts.itemCards);

  let coinRecords = [...(group.coinRecords ?? [])];
  let coinTotals = { ...(group.coinTotals ?? {}) };
  let itemInstances = [...(group.itemInstances ?? [])];
  const rewardedLevels = { ...(group.rewardedLevels ?? {}) };
  const memberRewarded = new Set(rewardedLevels[memberId] ?? []);

  const addCoin = (value: number, memo: string) => {
    if (!value) return;
    const rec: MemberCoinRecord = {
      id: newId('coin'),
      memberId,
      coinValue: value,
      coinType: 'Levelup',
      memo,
      recordedDate: date,
      createdAt: now,
    };
    coinRecords = [rec, ...coinRecords];
    coinTotals[memberId] = (coinTotals[memberId] ?? 0) + value;
  };

  const grantLevelReward = (level: number) => {
    const coinAmount = Number(levelCoins[level]) || 0;
    if (coinAmount > 0) {
      addCoin(coinAmount, `레벨 ${level} 달성 보상`);
    }

    const cardIds = levelItemCards[level] ?? [];
    cardIds.forEach((itemCardId) => {
      const existing = itemInstances.find(
        (it) =>
          it.memberId === memberId &&
          it.itemCardId === itemCardId &&
          it.category === 'levelup' &&
          it.acquiredLevel === level
      );
      if (existing) {
        const card = catalog.get(itemCardId);
        const nextRemaining =
          typeof existing.remainingUseCount === 'number'
            ? existing.remainingUseCount
            : remainingUseCountForNewItem(card);
        itemInstances = itemInstances.map((it) =>
          it.id === existing.id
            ? { ...it, isLocked: false, remainingUseCount: nextRemaining }
            : it
        );
        return;
      }
      itemInstances = [grantLevelItemInstance(memberId, itemCardId, level, now, catalog), ...itemInstances];
    });

    // 설정에 없는 구 아이템도 같은 레벨이면 잠금 해제
    itemInstances = itemInstances.map((it) => {
      if (
        it.memberId === memberId &&
        it.category === 'levelup' &&
        it.acquiredLevel === level &&
        it.isLocked
      ) {
        return { ...it, isLocked: false };
      }
      return it;
    });

    memberRewarded.add(level);
  };

  if (newLevel > oldLevel) {
    // 현재 레벨 이하 레벨업 아이템 잠금 해제
    itemInstances = itemInstances.map((it) => {
      if (
        it.memberId === memberId &&
        it.category === 'levelup' &&
        it.isLocked &&
        typeof it.acquiredLevel === 'number' &&
        it.acquiredLevel <= newLevel
      ) {
        return { ...it, isLocked: false };
      }
      return it;
    });

    for (let level = oldLevel + 1; level <= newLevel; level++) {
      const already = memberRewarded.has(level);
      const hasLockedForLevel = itemInstances.some(
        (it) =>
          it.memberId === memberId &&
          it.category === 'levelup' &&
          it.acquiredLevel === level &&
          it.isLocked
      );

      if (!already) {
        // 최초 도달 또는 회수 후 재도달(rewardedLevels에서 제거된 경우)
        grantLevelReward(level);
      } else if (hasLockedForLevel) {
        // 레거시: 회수 후에도 rewardedLevels가 남은 경우 → 코인 재지급 + 잠금 해제
        grantLevelReward(level);
      } else {
        // 이미 보유 중: 아이템만 한 번 더 잠금 해제(누락 보정)
        itemInstances = itemInstances.map((it) => {
          if (
            it.memberId === memberId &&
            it.category === 'levelup' &&
            it.acquiredLevel === level &&
            it.isLocked
          ) {
            return { ...it, isLocked: false };
          }
          return it;
        });
      }
    }
  } else {
    for (let level = oldLevel; level > newLevel; level--) {
      const coinAmount = Number(levelCoins[level]) || 0;
      const hasLevelItems = itemInstances.some(
        (it) =>
          it.memberId === memberId &&
          it.category === 'levelup' &&
          it.acquiredLevel === level
      );
      if (!memberRewarded.has(level) && coinAmount <= 0 && !hasLevelItems) {
        continue;
      }

      if (coinAmount > 0) {
        const current = coinTotals[memberId] ?? 0;
        const reclaim = Math.min(current, coinAmount);
        if (reclaim > 0) {
          addCoin(-reclaim, `레벨 ${level} 달성 보상 회수`);
        }
      }

      itemInstances = itemInstances.map((it) => {
        if (
          it.memberId === memberId &&
          it.category === 'levelup' &&
          it.acquiredLevel === level
        ) {
          return { ...it, isLocked: true };
        }
        return it;
      });
      // 회수 후 재레벨업 시 재지급되도록 rewardedLevels에서 제거
      memberRewarded.delete(level);
    }
  }

  rewardedLevels[memberId] = Array.from(memberRewarded).sort((a, b) => a - b);

  return {
    ...group,
    coinRecords,
    coinTotals,
    itemInstances,
    rewardedLevels,
  };
}

/**
 * 레벨 설정 변경 후 전 멤버 보상 재조정.
 *
 * - 이미 도달한 레벨 중 미지급 레벨 → 새 설정대로 지급
 * - 코인 금액 변경 → 기존 금액 회수(−) 후 새 금액 지급(+) (음수 잔액 허용)
 * - 아이템 추가 → 새로 지급
 * - 아이템 제거 → 인스턴스 삭제
 * - 아이템 유지 → 그대로
 */
export function reconcileLevelRewardsOnSettingsChange(
  group: GroupMemberPoints,
  prevSettings: PointLevelSettings,
  newSettings: PointLevelSettings,
  itemCards: ItemCardSet
): GroupMemberPoints {
  const unit = newSettings.levelUpPoints || 50;
  const date = todayIso();
  const now = new Date().toISOString();
  let changed = false;
  const affectedLevels = changedRewardLevels(prevSettings, newSettings);
  if (affectedLevels !== null && affectedLevels.length === 0) return group;

  let coinRecords = [...(group.coinRecords ?? [])];
  let coinTotals = { ...(group.coinTotals ?? {}) };
  let itemInstances = [...(group.itemInstances ?? [])];
  const rewardedLevels = { ...(group.rewardedLevels ?? {}) };

  const prevCoins = prevSettings.levelCoins ?? {};
  const newCoins = newSettings.levelCoins ?? {};
  const prevItems = prevSettings.levelItemCards ?? {};
  const newItems = newSettings.levelItemCards ?? {};
  const catalog = catalogById(itemCards);

  const allMemberIds = Object.keys(group.totals ?? {});

  for (const memberId of allMemberIds) {
    const total = group.totals[memberId] ?? 0;
    const currentLevel = levelFromTotal(total, unit);
    const memberRewarded = new Set(rewardedLevels[memberId] ?? []);
    const levelsToCheck =
      affectedLevels === null
        ? Array.from({ length: currentLevel }, (_, idx) => idx + 1)
        : affectedLevels.filter((level) => level <= currentLevel || memberRewarded.has(level));
    if (levelsToCheck.length === 0) continue;

    for (const level of levelsToCheck) {
      const wasRewarded = memberRewarded.has(level);
      const prevCoinAmt = Number(prevCoins[level]) || 0;
      const newCoinAmt = Number(newCoins[level]) || 0;
      const prevCardIds = prevItems[level] ?? [];
      const newCardIds = newItems[level] ?? [];

      // --- 코인 ---
      if (!wasRewarded) {
        // 미지급 레벨: 새 금액 지급
        if (newCoinAmt > 0) {
          const rec: MemberCoinRecord = {
            id: newId('coin'),
            memberId,
            coinValue: newCoinAmt,
            coinType: 'Levelup',
            memo: `레벨 ${level} 달성 보상`,
            recordedDate: date,
            createdAt: now,
          };
          coinRecords = [rec, ...coinRecords];
          coinTotals[memberId] = (coinTotals[memberId] ?? 0) + newCoinAmt;
          changed = true;
        }
      } else if (prevCoinAmt !== newCoinAmt) {
        // 이미 지급 + 금액 변경: 기존 회수 → 새 지급
        if (prevCoinAmt > 0) {
          const reclaim: MemberCoinRecord = {
            id: newId('coin'),
            memberId,
            coinValue: -prevCoinAmt,
            coinType: 'Levelup',
            memo: `레벨 ${level} 보상 변경 회수`,
            recordedDate: date,
            createdAt: now,
          };
          coinRecords = [reclaim, ...coinRecords];
          coinTotals[memberId] = (coinTotals[memberId] ?? 0) - prevCoinAmt;
          changed = true;
        }
        if (newCoinAmt > 0) {
          const grant: MemberCoinRecord = {
            id: newId('coin'),
            memberId,
            coinValue: newCoinAmt,
            coinType: 'Levelup',
            memo: `레벨 ${level} 달성 보상`,
            recordedDate: date,
            createdAt: now,
          };
          coinRecords = [grant, ...coinRecords];
          coinTotals[memberId] = (coinTotals[memberId] ?? 0) + newCoinAmt;
          changed = true;
        }
      }

      // --- 아이템 ---
      const prevSet = new Set(prevCardIds);
      const newSet = new Set(newCardIds);

      // 제거된 카드: 인스턴스 삭제
      for (const cardId of Array.from(prevSet)) {
        if (!newSet.has(cardId)) {
          const prevLen = itemInstances.length;
          itemInstances = itemInstances.filter(
            (it) =>
              !(
                it.memberId === memberId &&
                it.itemCardId === cardId &&
                it.category === 'levelup' &&
                it.acquiredLevel === level
              )
          );
          if (itemInstances.length !== prevLen) changed = true;
        }
      }

      // 추가된 카드: 새로 지급
      for (const cardId of Array.from(newSet)) {
        if (prevSet.has(cardId)) continue; // 유지
        if (wasRewarded) {
          // 이미 보상받았지만 카드가 추가된 경우
          const exists = itemInstances.some(
            (it) =>
              it.memberId === memberId &&
              it.itemCardId === cardId &&
              it.category === 'levelup' &&
              it.acquiredLevel === level
          );
          if (exists) continue;
        }
        const inst = grantLevelItemInstance(memberId, cardId, level, now, catalog);
        itemInstances = [inst, ...itemInstances];
        changed = true;
      }

      // 미지급 레벨의 기존 카드도 지급
      if (!wasRewarded) {
        for (const cardId of Array.from(newSet)) {
          if (prevSet.has(cardId)) {
            // prevSet에 있었지만 미지급이므로 지급 필요
            const exists = itemInstances.some(
              (it) =>
                it.memberId === memberId &&
                it.itemCardId === cardId &&
                it.category === 'levelup' &&
                it.acquiredLevel === level
            );
            if (!exists) {
              const inst = grantLevelItemInstance(memberId, cardId, level, now, catalog);
              itemInstances = [inst, ...itemInstances];
              changed = true;
            }
          }
        }
      }

      if (!memberRewarded.has(level)) {
        memberRewarded.add(level);
        changed = true;
      }
    }

    const nextRewarded = Array.from(memberRewarded).sort((a, b) => a - b);
    const prevRewarded = rewardedLevels[memberId] ?? [];
    if (
      prevRewarded.length !== nextRewarded.length ||
      prevRewarded.some((level, idx) => level !== nextRewarded[idx])
    ) {
      rewardedLevels[memberId] = nextRewarded;
      changed = true;
    }
  }

  if (!changed) return group;

  return {
    ...group,
    coinRecords,
    coinTotals,
    itemInstances,
    rewardedLevels,
  };
}

/** 여러 멤버의 포인트 합계가 바뀐 뒤 일괄 sync */
export function syncLevelUpRewardsForMembers(
  group: GroupMemberPoints,
  memberOldTotals: Record<string, number>,
  opts: {
    levelSettings: PointLevelSettings;
    itemCards: ItemCardSet;
    recordedDate?: string;
  }
): GroupMemberPoints {
  let next = group;
  Object.entries(memberOldTotals).forEach(([memberId, oldTotal]) => {
    const newTotal = next.totals[memberId] ?? 0;
    if (oldTotal === newTotal) return;
    next = syncLevelUpRewardsForMember(next, memberId, {
      oldTotal,
      newTotal,
      levelSettings: opts.levelSettings,
      itemCards: opts.itemCards,
      recordedDate: opts.recordedDate,
    });
  });
  return next;
}
